import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { Button } from '@mes-amis/galette-design-system';
import db from '@/db/db';
import { sortCaseNotes } from '@/db/types/case_note';
import { useCurrentPerson } from '@/hooks/useCurrentPerson';
import NoteCard from '../notes/NoteCard';

interface NotesTabProps {
  clientId: number;
  clientSlug: string;
}

/** A client's notes, read from this device. */
export default function NotesTab({ clientId, clientSlug }: NotesTabProps) {
  const navigate = useNavigate();
  const { currentPerson, organizationId } = useCurrentPerson();

  const notes = useLiveQuery(async () => {
    if (!organizationId) return [];
    const rows = await db.caseNotes.where('[client_id+organization_id]').equals([clientId, organizationId]).toArray();
    return sortCaseNotes(rows);
  }, [clientId, organizationId]);

  const options = useLiveQuery(
    async () => (organizationId ? (await db.noteOptions.get(organizationId)) ?? null : null),
    [organizationId],
  );

  // No options cached yet (never refreshed online): the form explains that.
  const canWrite = options === null || (options?.note_types.length ?? 0) > 0;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center gap-3">
        <h2 className="font-heading text-2xl text-black">Notes</h2>
        {canWrite && (
          <Button variant="primary" icon="plus" onClick={() => navigate(`/clients/${clientSlug}/notes/new`)}>
            New note
          </Button>
        )}
      </div>

      {notes === undefined ? null : notes.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500" data-testid="notes-empty">
          <p className="font-medium">No notes for this client yet.</p>
          {canWrite && <p className="text-sm mt-2">Tap &quot;New note&quot; to write one. It saves on this device and uploads when you&apos;re online.</p>}
        </div>
      ) : (
        <div className="space-y-3" data-testid="notes-list">
          {notes.map(note => (
            <NoteCard
              key={String(note.id)}
              note={note}
              options={options}
              currentPersonId={currentPerson?.id}
              onOpen={() => navigate(`/clients/${clientSlug}/notes/${note.id}`)}
            />
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500 text-center">
        This device keeps notes from the last 12 months and pinned notes. Older notes are in Mon Ami.
      </p>
    </div>
  );
}
